import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLButtonElement} [buyNowButton] - Sticky bar's buy-it-now button
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
/**
 * The theme's "Custom" size control: a <select> inside the variant picker that
 * submits as a line item property rather than selecting a variant. It is owned
 * by variant-main-picker.liquid and driven by custom-size.js.
 */
const CUSTOM_SIZE_SELECT = '.variant-option__custom-select';

/** Marks sticky options that map to a custom size rather than a variant. */
const CUSTOM_PREFIX = 'custom:';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #pendingCheckout = false;

  /** @type {MutationObserver | null} */
  #customSizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('change', this.#handleCustomSizeChange, { signal, capture: true });

    this.#getInitialQuantity();

    // The variant picker renders its custom-size control on its own schedule,
    // so mirror after the current frame rather than racing it.
    requestAnimationFrame(() => {
      if (signal.aborted) return;
      this.#syncCustomSizes();
    });

    this.#watchCustomSizeRestore();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#customSizeObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Mirrors an option choice made in the sticky bar onto the main variant picker,
   * which owns variant state. The picker's update then morphs this bar back with
   * the new image, price and selection.
   *
   * @param {Event} event - The change event from a sticky option select
   */
  handleOptionChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const value = select?.value;
    if (!value) return;

    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) return;

    // A custom size is a line item property, not a variant: drive the theme's
    // own select so custom-size.js persists it and the CSS de-emphasises the
    // real radio. Its inline onchange stops propagation, so the picker will not
    // re-render and this bar keeps the choice on screen.
    if (value.startsWith(CUSTOM_PREFIX)) {
      const customSelect = /** @type {HTMLSelectElement | null} */ (variantPicker.querySelector(CUSTOM_SIZE_SELECT));
      if (!customSelect) return;
      customSelect.value = value.slice(CUSTOM_PREFIX.length);
      customSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const target = variantPicker.querySelector(`[data-option-value-id="${value}"]`);
    if (!target) return;

    // The picker renders either radio buttons or a native select, depending on
    // the block's "variant style" setting. Drive whichever one is on the page.
    if (target instanceof HTMLOptionElement) {
      const pickerSelect = target.closest('select');
      if (!pickerSelect) return;
      pickerSelect.value = target.value;
      pickerSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (target instanceof HTMLInputElement) {
      target.click();
    }
  };

  /**
   * Copies the theme's custom-size choices into the matching sticky select and
   * reflects whichever choice is currently active.
   *
   * The choices come from a variant-picker block setting, which this section
   * cannot read in Liquid, so they are mirrored from the rendered DOM instead.
   * That also means the list stays correct when the setting changes.
   */
  #syncCustomSizes() {
    const pair = this.#getCustomSizeControls();
    if (!pair) return;
    const { customSelect, stickySelect } = pair;

    // The bar is re-rendered from the server on every variant change, so the
    // group is rebuilt each time rather than assumed to still be there.
    stickySelect.querySelector('optgroup[data-custom-sizes]')?.remove();

    const choices = [...customSelect.options].filter((option) => option.value !== '');
    if (!choices.length) return;

    const group = document.createElement('optgroup');
    group.setAttribute('data-custom-sizes', 'true');
    group.label = customSelect.options[0]?.textContent?.trim() || 'Custom';

    for (const choice of choices) {
      const option = document.createElement('option');
      option.value = `${CUSTOM_PREFIX}${choice.value}`;
      option.textContent = choice.textContent;
      group.appendChild(option);
    }
    stickySelect.appendChild(group);

    this.#reflectCustomSize();
  }

  /**
   * Re-reads the custom size once custom-size.js has restored it.
   *
   * On a fresh page load that script arrives late - it is a lazy module loaded
   * with fetchpriority="low" - and restores the saved size from sessionStorage
   * by assigning `select.value`, which fires no event. So a bar that only reads
   * on connect sees an empty select and falls back to the checked radio, and
   * nothing ever corrects it.
   *
   * The restore happens in the same synchronous pass that sets the element's
   * data-custom-size-bound flag, so watching for that attribute lands just
   * after the value is in place. Kept alive rather than one-shot, because each
   * variant re-render recreates the select and binds it again.
   */
  #watchCustomSizeRestore() {
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) return;

    this.#customSizeObserver = new MutationObserver(() => this.#reflectCustomSize());
    this.#customSizeObserver.observe(variantPicker, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-custom-size-bound'],
    });
  }

  /**
   * Pairs the theme's custom-size select with the sticky select for the same
   * option, or null when this product has no custom sizing.
   *
   * @returns {{ customSelect: HTMLSelectElement, stickySelect: HTMLSelectElement } | null}
   */
  #getCustomSizeControls() {
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const customSelect = /** @type {HTMLSelectElement | null} */ (
      variantPicker?.querySelector(CUSTOM_SIZE_SELECT) ?? null
    );
    if (!customSelect) return null;

    // The custom select carries no option name of its own; take it from a real
    // radio in the same fieldset.
    const optionName = customSelect
      .closest('fieldset')
      ?.querySelector('input[data-option-name]')
      ?.getAttribute('data-option-name');
    if (!optionName) return null;

    const stickySelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector(`.sticky-add-to-cart__option-select[data-option-name="${CSS.escape(optionName)}"]`)
    );
    if (!stickySelect) return null;

    return { customSelect, stickySelect };
  }

  /**
   * Points the sticky select at whatever the shopper actually chose: the custom
   * size when one is set, otherwise the checked variant radio. The radio stays
   * checked either way, because it is what carries the variant id.
   */
  #reflectCustomSize() {
    const pair = this.#getCustomSizeControls();
    if (!pair) return;
    const { customSelect, stickySelect } = pair;

    if (customSelect.value) {
      stickySelect.value = `${CUSTOM_PREFIX}${customSelect.value}`;
      return;
    }

    const checked = customSelect
      .closest('fieldset')
      ?.querySelector('input[data-option-name]:checked')
      ?.getAttribute('data-option-value-id');
    if (checked) stickySelect.value = checked;
  }

  /**
   * Mirrors a custom size chosen in the main picker onto the sticky bar.
   *
   * Capture phase is required: the theme's select carries an inline
   * `onchange="event.stopPropagation()"`, so by the bubble phase this event no
   * longer exists outside that element. Capture listeners on ancestors run
   * before it.
   *
   * @param {Event} event
   */
  #handleCustomSizeChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches(CUSTOM_SIZE_SELECT)) return;
    if (target.closest(`variant-picker[data-product-id="${this.dataset.productId}"]`) == null) return;

    this.#reflectCustomSize();
  };

  /**
   * Adds the current variant and sends the shopper straight to checkout.
   * Puppets the real add-to-cart button so line item properties (engraving,
   * quantity rules, gift card fields) come along, then redirects once the
   * cart request resolves.
   */
  handleBuyNowClick = () => {
    if (!this.#targetAddToCartButton) return;

    this.#pendingCheckout = true;
    this.refs.buyNowButton?.setAttribute('aria-busy', 'true');
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        // The morph replaced the sticky select with server markup, which knows
        // nothing about the custom sizes. Re-mirror them, then again next frame:
        // custom-size.js restores its own value from sessionStorage in a rAF, so
        // reading it synchronously here can catch it mid-restore.
        this.#syncCustomSizes();
        requestAnimationFrame(() => this.#reflectCustomSize());

        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    const checkoutPending = this.#pendingCheckout;
    this.#pendingCheckout = false;

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);

      if (checkoutPending) {
        event.promise
          .then(() => {
            window.location.href = `${Theme.routes.cart_url}/checkout`;
          })
          .catch(() => this.#clearBuyNowBusy());
      }
    } else {
      resetPuppet();
      if (checkoutPending) this.#clearBuyNowBusy();
    }
  };

  /**
   * Releases the buy-it-now button after a failed add, so it can be retried.
   */
  #clearBuyNowBusy() {
    this.refs.buyNowButton?.removeAttribute('aria-busy');
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
